/**
 * Event Service - Centralized event logging for Solana USDC Payment System
 * 事件服务 - Solana USDC 支付系统的集中式事件日志记录
 */

import { AppLogAPIService, ActionType, ChainType } from './app-log-api-service';

// 单例模式的事件服务
class EventService {
  private appLogService: AppLogAPIService;
  private sessionId: string;
  private userId?: string;
  private username?: string;

  constructor() {
    this.appLogService = new AppLogAPIService();
    this.sessionId = this.generateSessionId();
  }

  /**
   * 设置用户信息
   */
  setUserInfo(userId?: string, username?: string) {
    this.userId = userId;
    this.username = username;
  }

  /**
   * 生成会话ID
   */
  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`;
  }

  /**
   * 记录页面访问
   */
  async logPageVisit(extra?: object) {
    try {
      await this.appLogService.logUserVisit({
        userId: this.userId,
        username: this.username,
        sessionId: this.sessionId,
        extra
      });
    } catch (error) {
      console.error('Failed to log page visit:', error);
    }
  }

  /**
   * 记录钱包连接
   */
  async logWalletConnect(walletAddress: string, walletType?: string, extra?: object) {
    try {
      await this.appLogService.logWalletConnect({
        userId: this.userId,
        username: this.username,
        walletAddress,
        walletType,
        extra
      });
    } catch (error) {
      console.error('Failed to log wallet connect:', error);
    }
  }

  /**
   * 记录钱包断开连接
   */
  async logWalletDisconnect(walletAddress?: string, extra?: object) {
    try {
      await this.appLogService.logUserAction({
        userId: this.userId,
        username: this.username,
        actionType: ActionType.DISCONNECT_WALLET,
        actionDetail: 'Disconnect wallet',
        walletAddress,
        extra
      });
    } catch (error) {
      console.error('Failed to log wallet disconnect:', error);
    }
  }

  /**
   * 记录支付授权
   */
  async logPaymentApproval(walletAddress: string, amount: number, transactionHash?: string, extra?: object) {
    try {
      await this.appLogService.logPaymentApproval({
        userId: this.userId,
        username: this.username,
        walletAddress,
        amount,
        tokenSymbol: 'USDC',
        transactionHash,
        extra
      });
    } catch (error) {
      console.error('Failed to log payment approval:', error);
    }
  }

  /**
   * 记录交易完成
   */
  async logTransactionComplete(
    walletAddress: string, 
    transactionHash: string, 
    amount: number, 
    extra?: object
  ) {
    try {
      await this.appLogService.logTransaction({
        userId: this.userId,
        username: this.username,
        walletAddress,
        transactionHash,
        amount,
        tokenSymbol: 'USDC',
        actionDetail: `Complete USDC payment transaction`,
        extra
      });
    } catch (error) {
      console.error('Failed to log transaction:', error);
    }
  }

  /**
   * 记录余额查询
   */
  async logBalanceCheck(
    walletAddress: string,
    usdcBalance?: number,
    solBalance?: number,
    extra?: object
  ) {
    try {
      await this.appLogService.logUserAction({
        userId: this.userId,
        username: this.username,
        actionType: ActionType.API_CALL,
        actionDetail: 'Check USDC balance',
        walletAddress,
        extra: {
          usdcBalance,
          solBalance,
          tokenSymbol: 'USDC',
          ...extra
        }
      });
    } catch (error) {
      console.error('Failed to log balance check:', error);
    }
  }

  /**
   * 记录错误
   */
  async logError(
    errorType: string, 
    errorMessage: string, 
    walletAddress?: string, 
    stackTrace?: string, 
    extra?: object
  ) {
    try {
      await this.appLogService.logError({
        userId: this.userId,
        username: this.username,
        errorType,
        errorMessage,
        stackTrace,
        walletAddress,
        extra
      });
    } catch (error) {
      console.error('Failed to log error:', error);
    }
  }

  /**
   * 记录点击事件
   */
  async logClick(elementId: string, elementText?: string, extra?: object) {
    try {
      await this.appLogService.logUserAction({
        userId: this.userId,
        username: this.username,
        actionType: ActionType.CLICK,
        actionDetail: `Click ${elementId}`,
        extra: {
          elementId,
          elementText,
          ...extra
        }
      });
    } catch (error) {
      console.error('Failed to log click:', error);
    }
  }

  /**
   * 记录性能指标
   */
  async logPerformance(metric: string, value: number, unit: string, extra?: object) {
    try {
      await this.appLogService.logUserAction({
        userId: this.userId,
        username: this.username,
        actionType: ActionType.PERFORMANCE,
        actionDetail: `Performance metric: ${metric}`,
        extra: {
          metric,
          value,
          unit,
          timestamp: Date.now(),
          ...extra
        }
      });
    } catch (error) {
      console.error('Failed to log performance:', error);
    }
  }

  /**
   * 测试连接
   */
  async testConnection(): Promise<boolean> {
    try {
      return await this.appLogService.testConnection();
    } catch (error) {
      console.error('Failed to test connection:', error);
      return false;
    }
  }

  /**
   * 获取会话ID
   */
  getSessionId(): string {
    return this.sessionId;
  }
}

// 导出单例实例
export const eventService = new EventService();
export { ActionType, ChainType } from './app-log-api-service';
